'use client';
import throttle from 'lodash.throttle';
import { useState, useEffect } from 'react';

const SCROLL_DIRECTION = {
  UP: 'UP',
  DOWN: 'DOWN',
  LEFT: 'LEFT',
  RIGHT: 'RIGHT'
};
function isScrollingUp(positionY, previousY) {
  return positionY > previousY;
}
function getMaxScrollY(scrollTarget) {
  if (scrollTarget) {
    return scrollTarget.scrollHeight - scrollTarget.clientHeight;
  } else {
    const body = document.body;
    const html = document.documentElement;
    const documentHeight = Math.max(body.scrollHeight, body.offsetHeight, html.clientHeight, html.scrollHeight, html.offsetHeight);
    return documentHeight - window.innerHeight;
  }
}
function getScrollY(scrollTarget) {
  if (scrollTarget) {
    return Math.min(scrollTarget.scrollTop, getMaxScrollY(scrollTarget));
  } else {
    return Math.min(window.scrollY, getMaxScrollY());
  }
}
function useScrollY() {
  let {
    initialDirection = SCROLL_DIRECTION.UP,
    scrollTarget,
    wait,
    threshold
  } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  const [direction, setDirection] = useState(initialDirection);
  const [position, setPosition] = useState(0);
  const [changed, setChanged] = useState(false);
  const [isPassed, setIsPassed] = useState(false);
  useEffect(() => {
    let previousY = getScrollY(scrollTarget);
    const handleScroll = throttle(() => {
      const currentY = getScrollY(scrollTarget);
      if (currentY !== previousY) {
        setDirection(isScrollingUp(currentY, previousY) ? SCROLL_DIRECTION.UP : SCROLL_DIRECTION.DOWN);
        setPosition(currentY);
        previousY = currentY > 0 ? currentY : 0;
        setChanged(true);
      } else {
        setChanged(false);
      }
      if (typeof threshold === 'number') {
        setIsPassed(currentY > threshold);
      }
    }, wait);
    (scrollTarget ?? window).addEventListener('scroll', handleScroll);
    return () => (scrollTarget ?? window).removeEventListener('scroll', handleScroll);
  }, [scrollTarget, wait, threshold]);
  return {
    direction,
    position,
    changed,
    isPassed,
    getMaxScrollY
  };
}

export { SCROLL_DIRECTION, useScrollY as default };
